import * as XLSX from "xlsx";
import mammoth from "mammoth";

// PDF.js (legacy build + worker të bundle-uar)
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/build/pdf.worker.entry";

/* ================== API kryesore ================== */
export async function convertBlobToHtml(
  blob: Blob,
  ext: string
): Promise<string> {
  const ab = await blob.arrayBuffer();

  switch (ext) {
    case "pdf":
      return renderPdfToHtmlWithTextLayer(ab); // ✅ canvas + text-layer zyrtar
    case "docx":
      return renderDocxToHtml(ab);
    case "xlsx":
    case "xls":
      return renderXlsxToHtml(ab);
    case "rtf":
      return renderRtfToHtmlBasic(ab);
    case "txt":
      return renderTxtToHtml(ab);
    case "doc":
      throw new Error(
        ".doc nuk përkrahet në browser. Ju lutem përdorni .docx."
      );
    default:
      throw new Error(`Unsupported file type: ${ext}`);
  }
}

/* ---------- PDF: rendit text-layer sipas kolonave (jo sipas content-stream) ----------
 * pdf.js kthen textContent.items sipas rendit të brendshëm të PDF-it, i cili për
 * layout-e me shumë kolona (p.sh. LaTeX two-column) shpesh e "ndërthur" kolonën e
 * djathtë mes paragrafëve të kolonës së majtë. Range selection e browser-it ndjek
 * rendin e DOM-it, jo pozitën vizuale — kështu që pa këtë rirenditje, selektimi
 * "nga 1.2 te 2" mund të kapë edhe tekst të kolonës tjetër që bie mes tyre në DOM.
 * Rirenditja bëhet VETËM kur zbulohet një "gutter" bosh, i qendërzuar, që përshkon
 * pjesën më të madhe të lartësisë së faqes — sinjal i fortë i 2 kolonave reale;
 * përndryshe items mbetet i paprekur (dokumente me 1 kolonë nuk duhen prekur).
 */
function reorderTextItemsForColumns(items: any[], viewport: any): any[] {
  if (items.length < 8) return items;

  const boxes = items.map((item) => {
    const [x, y] = viewport.convertToViewportPoint(
      item.transform[4],
      item.transform[5]
    );
    const w = item.width || 0;
    const h =
      item.height || Math.hypot(item.transform[2], item.transform[3]) || 10;
    return { item, x0: x, x1: x + w, yTop: y - h, yBottom: y };
  });

  const pageWidth = viewport.width;
  const pageHeight = viewport.height;

  // Injorojmë header/footer (8% sipër/poshtë) kur kërkojmë gutter-in.
  const bodyTop = pageHeight * 0.08;
  const bodyBottom = pageHeight * 0.92;
  const bodyBoxes = boxes.filter(
    (b) => b.yBottom > bodyTop && b.yTop < bodyBottom
  );
  if (bodyBoxes.length < 8) return items;

  const BUCKETS = 200;
  const bucketWidth = pageWidth / BUCKETS;
  const covered = new Array(BUCKETS).fill(false);
  bodyBoxes.forEach((b) => {
    const start = Math.max(0, Math.floor(b.x0 / bucketWidth));
    const end = Math.min(BUCKETS - 1, Math.ceil(b.x1 / bucketWidth));
    for (let i = start; i <= end; i++) covered[i] = true;
  });

  // Gjej gap-in më të gjerë brenda 15%-85% të gjerësisë, afër qendrës.
  const midBucket = BUCKETS / 2;
  let bestGap: { start: number; end: number } | null = null;
  let i = 0;
  while (i < BUCKETS) {
    if (covered[i]) {
      i++;
      continue;
    }
    let j = i;
    while (j < BUCKETS && !covered[j]) j++;
    const start = i;
    const end = j - 1;
    const center = (start + end) / 2;
    const wideEnough = end - start >= 2; // ~1% e gjerësisë së faqes
    const centered = Math.abs(center - midBucket) < BUCKETS * 0.2;
    const notAtEdges = start > BUCKETS * 0.15 && end < BUCKETS * 0.85;
    if (wideEnough && centered && notAtEdges) bestGap = { start, end };
    i = j;
  }

  if (!bestGap) return items; // s'ka gutter të qartë -> mos e prek rendin

  const splitX = ((bestGap.start + bestGap.end + 1) / 2) * bucketWidth;

  const header = boxes.filter((b) => b.yBottom <= bodyTop);
  const footer = boxes.filter((b) => b.yTop >= bodyBottom);
  const body = boxes.filter((b) => b.yBottom > bodyTop && b.yTop < bodyBottom);
  const left = body.filter((b) => (b.x0 + b.x1) / 2 < splitX);
  const right = body.filter((b) => (b.x0 + b.x1) / 2 >= splitX);

  const byReadingOrder = (a: any, b: any) => {
    if (Math.abs(a.yTop - b.yTop) > 3) return a.yTop - b.yTop;
    return a.x0 - b.x0;
  };
  header.sort(byReadingOrder);
  left.sort(byReadingOrder);
  right.sort(byReadingOrder);
  footer.sort(byReadingOrder);

  return [...header, ...left, ...right, ...footer].map((b) => b.item);
}

/* ---------- PDF: canvas + text-layer (selektueshëm) ---------- */
async function renderPdfToHtmlWithTextLayer(
  arrayBuffer: ArrayBuffer
): Promise<string> {
  // ngarko dokumentin
  const pdf = await (pdfjsLib as any).getDocument({ data: arrayBuffer })
    .promise;

  // CSS minimale – e mban text-layerin sipër canvas-it pa prishur asgjë
  let html = `
<style>
  .pdf-page{
    position:relative;
    margin:0 auto 16px;
    background:#fff;
  }
  /* ✅ imazhi vetëm për pamje, mos marrë klik/drag */
  .pdf-canvas{
    position:absolute; left:0; top:0;
    display:block;
    width:auto; height:auto;
    pointer-events:none;
    z-index:1;
  }
  /* ✅ text-layer sipër canvas-it dhe selektueshëm */
  .textLayer{
    position:absolute; inset:0;
    z-index:2;
    pointer-events:auto;
    user-select:text; -webkit-user-select:text;
  }
  .textLayer > span{
    position:absolute; white-space:pre; transform-origin:0 0;
    line-height:1; margin:0; padding:0; border:0; background:transparent;
    /* e mbajmë “invisible” që të shihet pdf-i poshtë, por selektimi punon */
    color: transparent; -webkit-text-fill-color: transparent;
    user-select:text; -webkit-user-select:text;
  }
  ::selection { background: rgba(160,114,235,.25); }
</style>`;

  // do të përdorim një kontejner off-DOM për të ndërtuar HTML-in
  const tmp = document.createElement("div");

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    // viewport bazë – scaling e bën container-i yt (fit width) në iframe
    const viewport = page.getViewport({ scale: 1 });

    // 1) rendero faqen në canvas (mban fotot, figurat, charts, gjithçka)
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    // 2) ndërto text-layer-in zyrtar (PDF.js e llogarit vet pozicionimin)
    const textContent = await page.getTextContent();
    textContent.items = reorderTextItemsForColumns(textContent.items, viewport);

    const pageWrap = document.createElement("div");
    pageWrap.className = "pdf-page";
    pageWrap.style.width = `${viewport.width}px`;
    pageWrap.style.height = `${viewport.height}px`;

    // canvas (si <img> për output HTML)
    const img = document.createElement("img");
    img.className = "pdf-canvas";
    img.width = Math.ceil(viewport.width);
    img.height = Math.ceil(viewport.height);
    img.src = canvas.toDataURL("image/png"); // embed si data URL
    pageWrap.appendChild(img);

    // shto textLayer container
    const textLayerDiv = document.createElement("div");
    textLayerDiv.className = "textLayer";
    pageWrap.appendChild(textLayerDiv);

    // përdor renderTextLayer API (është pjesë e legacy build-it)
    // @ts-ignore
    await (pdfjsLib as any).renderTextLayer({
      textContent,
      container: textLayerDiv,
      viewport,
      textDivs: [], // mbushet nga renderer
      enhanceTextSelection: true,
    }).promise;

    tmp.appendChild(pageWrap);
  }

  html += tmp.innerHTML;
  return html;
}

/* ---------- DOCX -> HTML ---------- */
async function renderDocxToHtml(ab: ArrayBuffer): Promise<string> {
  const { value } = await mammoth.convertToHtml(
    { arrayBuffer: ab },
    {
      styleMap: [
        "p[style-name='Title'] => h1:fresh",
        "p[style-name='Subtitle'] => h2:fresh",
      ],
    }
  );
  return value || "<div></div>";
}

/* ---------- XLS/XLSX -> HTML ---------- */
async function renderXlsxToHtml(ab: ArrayBuffer): Promise<string> {
  const wb = XLSX.read(ab, { type: "array" });
  return (
    wb.SheetNames.map((name, i) => {
      const ws = wb.Sheets[name];
      const table = XLSX.utils.sheet_to_html(ws, {
        id: `sheet-${i}`,
        editable: false,
      });
      return `<h3>${escapeHtml(name)}</h3>${table}`;
    }).join("") || "<div></div>"
  );
}

/* ---------- RTF -> HTML (fallback plaintext) ---------- */
function renderRtfToHtmlBasic(ab: ArrayBuffer): string {
  const rtf = new TextDecoder("utf-8").decode(new Uint8Array(ab));
  const text = rtfToPlainText(rtf);
  return `<pre style="white-space:pre-wrap;margin:0">${escapeHtml(text)}</pre>`;
}

function rtfToPlainText(rtf: string): string {
  return rtf
    .replace(/\\par[d]?/g, "\n")
    .replace(/\\line/g, "\n")
    .replace(/\\tab/g, "\t")
    .replace(/\\'[0-9a-fA-F]{2}/g, (m) =>
      String.fromCharCode(parseInt(m.slice(2), 16))
    )
    .replace(/\\[a-zA-Z]+-?\d* ?/g, "")
    .replace(/{\\\*?[^{}]*}|[{}]/g, "")
    .replace(/\r?\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/* ---------- TXT -> HTML ---------- */
function renderTxtToHtml(ab: ArrayBuffer): string {
  const text = new TextDecoder("utf-8").decode(new Uint8Array(ab));
  return `<pre style="white-space:pre-wrap;margin:0">${escapeHtml(text)}</pre>`;
}

function escapeHtml(s: string) {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!)
  );
}
